/**
 * GTM DataLayer Event Tracking
 *
 * Centralized module for pushing custom events to the GTM dataLayer.
 * Only active in production when GTM is loaded.
 *
 * Events implemented:
 * - page_viewed: every page load
 * - view_resource: resource/blog page loads
 * - select_resource: click on resource cards in listings
 * - form_submitted: HubSpot form submissions
 * - search_initiated: site search execution
 * - cta_clicked: CTA button clicks
 * - header_menu_click: header navigation link clicks
 * - footer_menu_click: footer navigation link clicks
 * - video_played: video play start
 * - video_paused: video pause
 * - video_progressed: video progress milestones (10%, 25%, 50%, 75%)
 * - video_completed: video finished
 */

(function() {
  'use strict';
  /* eslint-disable camelcase */

  // Only run when GTM dataLayer is available (production)
  if (!window.dataLayer) return;

  // --- Dynamic Referrer Tracking ---
  // Store the internal referrer robustly via sessionStorage to bypass browser Referrer-Policy
  // or new-tab drops that might cause document.referrer to appear static or missing.
  let dynamicReferrer = document.referrer;
  try {
    const currentUrl = window.location.href;
    const storedCurrent = sessionStorage.getItem('cp_current_url');
    const ownHost = window.location.hostname;

    // Detect if the user arrived from an external site (e.g. Google, LinkedIn)
    let isExternalReferrer = false;
    if (document.referrer) {
      try {
        const refHost = new URL(document.referrer).hostname;
        isExternalReferrer = refHost !== ownHost;
      } catch (_) {/* malformed referrer */}
    }

    if (isExternalReferrer) {
      // User arrived from outside — trust the browser's referrer over stale storage
      sessionStorage.setItem('cp_prev_url', document.referrer);
    } else if (storedCurrent && storedCurrent !== currentUrl) {
      // Internal navigation — shift the previous page into the referrer slot
      sessionStorage.setItem('cp_prev_url', storedCurrent);
    } else if (!storedCurrent) {
      // First page in a brand-new session
      sessionStorage.setItem('cp_prev_url', document.referrer);
    }
    sessionStorage.setItem('cp_current_url', currentUrl);

    dynamicReferrer = sessionStorage.getItem('cp_prev_url') || document.referrer;
  } catch (e) {
    // Graceful fallback if sessionStorage is disabled in browser
  }

  // --- Helpers ---

  /**
   * Returns common page-level data included in every event.
   * Derives page_category from URL path segments as a fallback when meta tag is generic.
   * @return {Object}
   */
  function getPageData() {
    const pageCategoryMeta = document.querySelector('meta[name="page-category"]');
    let pageCategory = pageCategoryMeta ? pageCategoryMeta.content : '';

    // Derive more specific category from URL path if meta is generic
    if (!pageCategory || pageCategory === 'page') {
      const pathSegments = window.location.pathname.split('/').filter(Boolean);
      if (pathSegments.length > 0) {
        // Use the most relevant path segment for category
        // e.g., /resources/whitepapers/slug → 'whitepapers'
        // e.g., /about/ → 'about'
        pageCategory = pathSegments.length > 1 ? pathSegments[pathSegments.length - 2] : pathSegments[0];
      }
    }

    return {
      page_url: window.location.href,
      page_title: document.title,
      referrer_url: dynamicReferrer,
      page_category: pageCategory,
    };
  }

  /**
   * Push an event and its params to the GTM dataLayer.
   * @param {string} eventName
   * @param {Object} params
   */
  function pushEvent(eventName, params) {
    window.dataLayer.push(Object.assign({event: eventName}, params));
  }

  /**
   * Find the closest anchor element from a click target.
   * @param {Element} target
   * @param {Element} boundary - stop searching at this element
   * @return {HTMLAnchorElement|null}
   */
  function closestAnchor(target, boundary) {
    let el = target;
    while (el && el !== boundary) {
      if (el.tagName === 'A' && el.href) return el;
      el = el.parentElement;
    }
    return null;
  }

  /**
   * Extract visible text from a link element, stripping hidden spans.
   * @param {HTMLElement} el
   * @return {string}
   */
  function getLinkText(el) {
    // Prefer btn__text if available (for styled buttons)
    const btnText = el.querySelector('.btn__text');
    if (btnText) return btnText.textContent.trim();
    return el.textContent.trim();
  }

  /**
   * Check if an element is a video-related control (play button, embed, etc.)
   * @param {HTMLElement} el
   * @return {boolean}
   */
  function isVideoElement(el) {
    if (!el) return false;
    // Check if the element itself or ancestor is a video-related element
    return !!(
      el.closest('lite-youtube') ||
      el.closest('lite-vimeo') ||
      el.closest('.lite-video') ||
      el.closest('.video-player') ||
      el.closest('.video-container[data-js-module="Video"]')
    );
  }

  // --- 1. page_viewed ---
  pushEvent('page_viewed', getPageData());

  // --- 2. view_resource ---
  // Fires on resource detail pages that have resource meta tags
  const resourceTitleMeta = document.querySelector('meta[name="resource-title"]');
  if (resourceTitleMeta) {
    const resourceAuthorMeta = document.querySelector('meta[name="resource-author"]');
    const resourceDateMeta = document.querySelector('meta[name="resource-publish-date"]');
    pushEvent('view_resource', Object.assign({
      resource_title: resourceTitleMeta.content || '',
      resource_author: resourceAuthorMeta ? resourceAuthorMeta.content : '',
      resource_publish_date: resourceDateMeta ? resourceDateMeta.content : '',
    }, getPageData()));
  }

  // --- 3. select_resource ---
  // Delegated click on resource cards in post listings
  document.addEventListener('click', function(e) {
    // Match clicks within a paginatedPostsList card link, card with GTM data,
    // or card inside a list item with GTM data (useCards variant)
    const card = e.target.closest('.paginatedPostsList-card, .card[data-gtm-resource-title]') ||
                 (e.target.closest('.card') && e.target.closest('li[data-gtm-resource-title]'));
    if (!card) return;

    // Find the element with GTM data — could be the card itself or a parent li
    const dataSource = card.matches('[data-gtm-resource-title]') ?
      card :
      card.closest('[data-gtm-resource-title]');

    let resourceTitle = dataSource ? dataSource.getAttribute('data-gtm-resource-title') : '';
    const resourceAuthor = dataSource ? dataSource.getAttribute('data-gtm-resource-author') : '';
    const resourceDate = dataSource ? dataSource.getAttribute('data-gtm-resource-date') : '';

    // Fallback: extract title from the card content
    if (!resourceTitle) {
      const titleEl = card.querySelector('.paginatedPostsList-card__title, .card__title');
      resourceTitle = titleEl ? titleEl.textContent.trim() : '';
    }

    pushEvent('select_resource', Object.assign({
      resource_title: resourceTitle,
      resource_author: resourceAuthor,
      resource_publish_date: resourceDate,
    }, getPageData()));
  });

  // --- 4. form_submitted ---
  // Listen for HubSpot form submission callbacks via postMessage
  window.addEventListener('message', function(event) {
    if (
      event.data &&
      event.data.type === 'hsFormCallback' &&
      event.data.eventName === 'onFormSubmitted'
    ) {
      const formId = event.data.id || '';

      // Search for form container across all possible selectors
      const formContainer = document.querySelector(
        '[data-form-id="' + formId + '"]',
      ) || document.querySelector(
        '.hubspotForm[data-form-id="' + formId + '"]',
      );

      let formName = '';
      let formType = '';

      if (formContainer) {
        formName = formContainer.getAttribute('data-form-name') || '';
        formType = formContainer.getAttribute('data-form-type') || '';

        // Try to extract form name from the headline within the form section
        if (!formName) {
          const headlineEl = formContainer.querySelector('.headline__title');
          formName = headlineEl ? headlineEl.textContent.trim() : '';
        }
      }

      pushEvent('form_submitted', Object.assign({
        form_name: formName || formId,
        form_id: formId,
        form_type: formType || 'hubspot',
      }, getPageData()));
    }
  });

  // --- 5. search_initiated ---
  // Listen for custom event dispatched by Search module on search close
  window.addEventListener('gtm:search', function(e) {
    if (e.detail && e.detail.searchTerm) {
      pushEvent('search_initiated', Object.assign({
        search_term: e.detail.searchTerm,
      }, getPageData()));
    }
  });

  // --- 6. cta_clicked ---
  // Delegated click on CTA buttons (excluding header nav links, footer, and video controls)
  document.addEventListener('click', function(e) {
    // Match both .btn elements and regular links within main content
    const btn = e.target.closest('.btn') || e.target.closest('main a');
    if (!btn) return;

    // Skip if inside footer — those fire separate events
    if (btn.closest('.site-footer')) return;

    // Skip header nav links (regular menu items), but ALLOW header CTA buttons (.btn--primary)
    if (btn.closest('header.nav') && !btn.classList.contains('btn--primary')) return;

    // Skip video-related elements (play buttons, embeds, etc.)
    if (isVideoElement(btn) || isVideoElement(e.target)) return;

    // Skip non-navigational elements (pagination, search results, filters, etc.)
    if (btn.closest('.pagination') || btn.closest('.search__results')) return;
    if (btn.closest('.paginatedPostsList-categories') || btn.closest('.paginatedPostsList-tabs')) return;

    // Skip resource cards — those fire select_resource
    if (btn.closest('.paginatedPostsList-card') || btn.closest('.card[data-gtm-resource-title]')) return;

    // Skip non-navigational buttons (toggles, show-more, modals without URLs, etc.)
    const href = btn.href || btn.getAttribute('href');
    if (!href || href === '#' || href === '' || href.startsWith('javascript:')) return;
    if (href.startsWith('tel:') || href.startsWith('mailto:') || href.startsWith('fax:')) return;

    pushEvent('cta_clicked', Object.assign({
      click_text: getLinkText(btn),
      click_url: href,
    }, getPageData()));
  });

  // --- 7. header_menu_click ---
  // Delegated click on header navigation links (excluding CTA buttons)
  const headerEl = document.querySelector('header.nav');
  if (headerEl) {
    headerEl.addEventListener('click', function(e) {
      const link = closestAnchor(e.target, headerEl);
      if (!link) return;

      // Skip branding/logo link
      if (link.classList.contains('nav__branding')) return;

      // Skip CTA buttons in header — those fire cta_clicked instead
      if (link.classList.contains('btn--primary')) return;

      pushEvent('header_menu_click', Object.assign({
        menu_click_text: getLinkText(link),
        menu_click_url: link.href,
      }, getPageData()));
    });
  }

  // --- 8. footer_menu_click ---
  // Delegated click on footer links
  const footerEl = document.querySelector('.site-footer');
  if (footerEl) {
    footerEl.addEventListener('click', function(e) {
      const link = closestAnchor(e.target, footerEl);
      if (!link) return;

      // Skip branding/logo link
      if (link.classList.contains('site-footer__branding-link')) return;

      pushEvent('footer_menu_click', Object.assign({
        footer_click_text: getLinkText(link),
        footer_click_url: link.href,
      }, getPageData()));
    });
  }

  // --- Video Tracking (9-12) ---
  // Track milestones already reported per video to avoid duplicates
  const videoMilestones = new WeakMap();

  /**
   * Extract video metadata from a video element or its container.
   * @param {HTMLElement} videoEl - a <video>, <lite-youtube>, <lite-vimeo>, or iframe element
   * @return {Object}
   */
  function getVideoData(videoEl) {
    let name = '';
    let url = '';
    let type = 'video';

    if (videoEl.tagName === 'VIDEO') {
      const source = videoEl.querySelector('source');
      url = source ? source.src : (videoEl.src || '');
      type = 'mp4';

      // Try to get the video name from the poster or parent container
      const container = videoEl.closest('.video-container, .video');
      if (container) {
        const titleEl = container.querySelector('.headline__title, h2, h3');
        name = titleEl ? titleEl.textContent.trim() : '';
      }
      if (!name) {
        name = videoEl.getAttribute('title') || videoEl.getAttribute('aria-label') || '';
      }
    } else if (videoEl.tagName === 'LITE-YOUTUBE') {
      const videoId = videoEl.getAttribute('videoid');
      url = 'https://www.youtube.com/watch?v=' + videoId;
      type = 'youtube';
      name = videoEl.getAttribute('playlabel') ||
             videoEl.getAttribute('title') || '';
    } else if (videoEl.tagName === 'LITE-VIMEO') {
      url = videoEl.getAttribute('href') || '';
      type = 'vimeo';
      name = videoEl.getAttribute('playlabel') ||
             videoEl.getAttribute('title') || '';
    } else if (videoEl.tagName === 'IFRAME') {
      url = videoEl.src || '';
      if (url.includes('youtube')) {
        type = 'youtube';
      } else if (url.includes('vimeo')) {
        type = 'vimeo';
      }
    } else if (videoEl.tagName === 'A') {
      // Modal mock embed elements (e.g., <a class="lite-video">)
      url = videoEl.getAttribute('href') || '';
      if (url.includes('youtube')) type = 'youtube';
      else if (url.includes('vimeo')) type = 'vimeo';
      name = videoEl.getAttribute('title') || videoEl.getAttribute('aria-label') || '';
    }

    // Fallback: Try to get explicit tracking name from parent wrapper if still empty
    if (!name) {
      const container = videoEl.closest('.video-container, .video, section');
      if (container) {
        name = container.getAttribute('data-gtm-video-name') || '';
      }
    }

    // Last resort: extract filename from the video src URL
    if (!name && url) {
      try {
        const pathname = new URL(url).pathname;
        const filename = pathname.split('/').pop();
        if (filename) {
          name = decodeURIComponent(filename.replace(/\.[^.]+$/, '').replace(/[_-]/g, ' '));
        }
      } catch (_) {/* invalid URL */}
    }

    return {
      video_name: name,
      video_url: url,
      video_type: type,
    };
  }

  /**
   * Attach tracking listeners to a native <video> element.
   * @param {HTMLVideoElement} video
   */
  function trackNativeVideo(video) {
    if (video.hasAttribute('data-gtm-tracked')) return;
    video.setAttribute('data-gtm-tracked', 'true');

    video.addEventListener('play', function() {
      pushEvent('video_played', Object.assign(
        getVideoData(video), getPageData(),
      ));
    });

    video.addEventListener('pause', function() {
      // Do not fire paused if the video has ended
      if (video.ended) return;
      pushEvent('video_paused', Object.assign(
        getVideoData(video), getPageData(),
      ));
    });

    video.addEventListener('ended', function() {
      pushEvent('video_completed', Object.assign(
        getVideoData(video), getPageData(),
      ));
    });

    video.addEventListener('timeupdate', function() {
      if (!video.duration) return;

      const milestones = videoMilestones.get(video) || {};
      const percent = Math.floor((video.currentTime / video.duration) * 100);
      const checkpoints = [10, 25, 50, 75];

      checkpoints.forEach(function(cp) {
        if (percent >= cp && !milestones[cp]) {
          milestones[cp] = true;
          pushEvent('video_progressed', Object.assign(
            getVideoData(video),
            {video_progress: cp},
            getPageData(),
          ));
        }
      });

      videoMilestones.set(video, milestones);
    });
  }

  /**
   * Attach tracking to a lite-youtube or lite-vimeo embed.
   * After the embed activates and loads an iframe, track pause/progress/completed
   * via the YouTube/Vimeo Player APIs.
   * @param {HTMLElement} embed
   */
  function trackLiteEmbed(embed) {
    if (embed.hasAttribute('data-gtm-tracked')) return;
    embed.setAttribute('data-gtm-tracked', 'true');

    embed.addEventListener('click', function() {
      // Only fire once when the embed activates
      if (embed.classList.contains('gtm-play-tracked')) return;
      embed.classList.add('gtm-play-tracked');

      pushEvent('video_played', Object.assign(
        getVideoData(embed), getPageData(),
      ));

      // After activation, watch for the iframe to be inserted and attach
      // YouTube/Vimeo Player API listeners for pause/progress/completed
      if (embed.tagName === 'LITE-YOUTUBE') {
        waitForYouTubePlayer(embed);
      } else if (embed.tagName === 'LITE-VIMEO') {
        waitForVimeoPlayer(embed);
      }
    });
  }

  /**
   * Wait for the YouTube iframe to appear inside a lite-youtube element,
   * then use the YouTube IFrame API to track pause/progress/completed.
   * @param {HTMLElement} embed
   */
  function waitForYouTubePlayer(embed) {
    let attempts = 0;
    const checkInterval = setInterval(function() {
      const iframe = embed.querySelector('iframe');
      attempts++;
      if (iframe) {
        clearInterval(checkInterval);
        attachYouTubeTracking(iframe, embed);
      }
      // Stop checking after 10 seconds
      if (attempts > 20) clearInterval(checkInterval);
    }, 500);
  }

  /**
   * Attach YouTube IFrame API tracking to an iframe.
   * @param {HTMLIFrameElement} iframe
   * @param {HTMLElement} embed - the parent lite-youtube element for metadata
   */
  function attachYouTubeTracking(iframe, embed) {
    // Ensure the iframe src has enablejsapi=1
    if (iframe.src && !iframe.src.includes('enablejsapi=1')) {
      const separator = iframe.src.includes('?') ? '&' : '?';
      iframe.src = iframe.src + separator + 'enablejsapi=1&origin=' + window.location.origin;
    }

    // Load YouTube IFrame API if not already loaded
    if (!window.YT || !window.YT.Player) {
      if (!document.querySelector('script[src*="youtube.com/iframe_api"]')) {
        const tag = document.createElement('script');
        tag.src = 'https://www.youtube.com/iframe_api';
        document.head.appendChild(tag);
      }
    }

    // Wait for YT API to be ready
    let ytCheckAttempts = 0;
    const ytCheck = setInterval(function() {
      ytCheckAttempts++;
      if (window.YT && window.YT.Player) {
        clearInterval(ytCheck);
        try {
          const player = new window.YT.Player(iframe, {
            events: {
              onStateChange: function(event) {
                const videoData = getVideoData(embed);
                if (event.data === window.YT.PlayerState.PAUSED) {
                  pushEvent('video_paused', Object.assign(videoData, getPageData()));
                } else if (event.data === window.YT.PlayerState.ENDED) {
                  pushEvent('video_completed', Object.assign(videoData, getPageData()));
                }
              },
            },
          });

          // Track progress milestones via polling
          const ytMilestones = {};
          setInterval(function() {
            if (!player.getDuration || !player.getCurrentTime) return;
            try {
              const duration = player.getDuration();
              const currentTime = player.getCurrentTime();
              if (!duration) return;
              const pct = Math.floor((currentTime / duration) * 100);
              [10, 25, 50, 75].forEach(function(cp) {
                if (pct >= cp && !ytMilestones[cp]) {
                  ytMilestones[cp] = true;
                  pushEvent('video_progressed', Object.assign(
                    getVideoData(embed),
                    {video_progress: cp},
                    getPageData(),
                  ));
                }
              });
            } catch (err) {
              // Player might not be ready yet
            }
          }, 1000);
        } catch (err) {
          // iframe may not accept YT.Player constructor
        }
      }
      if (ytCheckAttempts > 20) clearInterval(ytCheck);
    }, 500);
  }

  /**
   * Wait for the Vimeo iframe to appear inside a lite-vimeo element,
   * then use the Vimeo Player SDK to track pause/progress/completed.
   * @param {HTMLElement} embed
   */
  function waitForVimeoPlayer(embed) {
    let attempts = 0;
    const checkInterval = setInterval(function() {
      const iframe = embed.querySelector('iframe');
      attempts++;
      if (iframe) {
        clearInterval(checkInterval);
        attachVimeoTracking(iframe, embed);
      }
      if (attempts > 20) clearInterval(checkInterval);
    }, 500);
  }

  /**
   * Attach Vimeo Player SDK tracking to an iframe.
   * @param {HTMLIFrameElement} iframe
   * @param {HTMLElement} embed - the parent lite-vimeo element for metadata
   */
  function attachVimeoTracking(iframe, embed) {
    // Load Vimeo Player SDK if not already loaded
    if (!window.Vimeo || !window.Vimeo.Player) {
      if (!document.querySelector('script[src*="player.vimeo.com/api"]')) {
        const tag = document.createElement('script');
        tag.src = 'https://player.vimeo.com/api/player.js';
        document.head.appendChild(tag);
      }
    }

    let vimeoCheckAttempts = 0;
    const vimeoCheck = setInterval(function() {
      vimeoCheckAttempts++;
      if (window.Vimeo && window.Vimeo.Player) {
        clearInterval(vimeoCheck);
        try {
          const player = new window.Vimeo.Player(iframe);
          const videoData = getVideoData(embed);

          player.on('pause', function() {
            pushEvent('video_paused', Object.assign(videoData, getPageData()));
          });

          player.on('ended', function() {
            pushEvent('video_completed', Object.assign(videoData, getPageData()));
          });

          // Track progress milestones
          const vimeoMilestones = {};
          player.on('timeupdate', function(data) {
            if (!data.duration) return;
            const pct = Math.floor((data.seconds / data.duration) * 100);
            [10, 25, 50, 75].forEach(function(cp) {
              if (pct >= cp && !vimeoMilestones[cp]) {
                vimeoMilestones[cp] = true;
                pushEvent('video_progressed', Object.assign(
                  getVideoData(embed),
                  {video_progress: cp},
                  getPageData(),
                ));
              }
            });
          });
        } catch (err) {
          // Player initialization failed
        }
      }
      if (vimeoCheckAttempts > 20) clearInterval(vimeoCheck);
    }, 500);
  }

  // Track existing videos on page load
  document.querySelectorAll('video').forEach(trackNativeVideo);
  document.querySelectorAll('lite-youtube, lite-vimeo').forEach(trackLiteEmbed);

  // Observe DOM for dynamically added videos (e.g., modals, lazy-loaded content)
  const observer = new MutationObserver(function(mutations) {
    mutations.forEach(function(mutation) {
      mutation.addedNodes.forEach(function(node) {
        if (node.nodeType !== 1) return;

        // Check if the added node itself is a video
        if (node.tagName === 'VIDEO') trackNativeVideo(node);
        if (node.tagName === 'LITE-YOUTUBE' || node.tagName === 'LITE-VIMEO') {
          trackLiteEmbed(node);
        }

        // Check children of the added node
        if (node.querySelectorAll) {
          node.querySelectorAll('video').forEach(trackNativeVideo);
          node.querySelectorAll('lite-youtube, lite-vimeo').forEach(trackLiteEmbed);
        }
      });
    });
  });

  observer.observe(document.body, {childList: true, subtree: true});
})();
